import React, { useEffect, useState, useMemo, memo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiTrash2, FiSave, FiEdit3, FiEye  } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from 'react-router-dom';
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import usePermission from "../../hooks/usePermission";



// 1) Status Switch Bileşeni

const TemplateStatusSwitch = memo(({ templateId, currentStatus, fetchTemplates }) => {
  const [isChecked, setIsChecked] = useState(currentStatus === "active");

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;
  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    // Switch'i anlık güncelle
    setIsChecked(!isChecked);

    try {
      // PUT /api/mail-templates/update-status/:id
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/mail-templates/update-status/${templateId}`, {
        status: newStatus,
      });
      // Tabloyu yeniden çek
      fetchTemplates();
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      toast.error(getText("ERROR_STATUS_UPDATE_FAILED"));
      // Hata alırsa geri döndür
      setIsChecked(isChecked);
    }
  };

  return (
    <div className="form-check form-switch d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      >
      </span>
    </div>
  );
});


// 2) Ana Bileşen: MailTemplates

const MailTemplates = () => {
  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/mail-templates`;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTemplate, setNewTemplate] = useState({
    title: "",
    content: "",
    status: "active",
  });
  const { canView, canEdit, canDelete, canCreate } = usePermission("static_mail_template");

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;

  const isTranslationsReady = Object.keys(translations).length > 0;

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(BASE_URL);
  
      // 🔥 Sadece silinmemiş (deleted_at NULL olan) şablonları al
      const activeTemplates = (resp.data || []).filter(template => !template.deleted_at);
  
      setTemplates(activeTemplates);
    } catch (error) {
      console.error("Şablonlar alınamadı:", error);
      toast.error(getText("ERROR_TEMPLATES_NOT_LOADED"));
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    fetchTemplates();
  }, []);

  const editTemplate = (templateData) => {
    console.log("Düzenle butonuna tıklanan şablon:", templateData);
  };
  
  const handleSoftDelete = (id) => {
    confirmAlert({
      title: getText("SOFT_DELETE_CONFIRM_TITLE", "Şablonu Çöp Kutusuna Taşı"),
      message: getText("SOFT_DELETE_CONFIRM_MSG", "Bu mail şablonunu çöpe taşımak istediğinize emin misiniz?"),
      buttons: [
        {
          label: getText("YES_SOFT_DELETE", "Evet, Taşı"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(`${BASE_URL}/soft-delete/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(getText("SUCCESS_TEMPLATE_SOFT_DELETED", "Şablon çöpe taşındı!"));
              fetchTemplates(); // Listeyi yenile
            } catch (error) {
              console.error("Soft delete hatası:", error);
              toast.error(getText("ERROR_TEMPLATE_SOFT_DELETE_FAILED", "Çöpe taşıma başarısız!"));
            }
          },
        },
        {
          label: getText("CANCEL", "İptal"),
          onClick: () => toast.info(getText("SOFT_DELETE_CANCELLED", "İşlem iptal edildi.")),
        },
      ],
    });
  };
  


  // Tablonun Kolonları
 
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: getText("TABLE_ID_LABEL"),
        size: 60,
      },
      {
        accessorKey: "title",
        header: getText("TABLE_TITLE_LABEL"),
        size: 200,
      },
      {
        accessorKey: "createdAt",
        header: getText("TABLE_CREATED_AT"),
        size: 130,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
        cell: (info) => {
          const raw = info.getValue();
          return new Date(raw).toLocaleDateString("tr-TR");
        },
      },
      {
        accessorKey: "updatedAt",
        header: getText("TABLE_UPDATED_AT"),
        size: 130,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
        cell: (info) => {
          const raw = info.getValue();
          return new Date(raw).toLocaleDateString("tr-TR");
        },
      },
      
      {
        accessorKey: "status",
        header: () => (
            <div style={{ textAlign: 'end', width: '130px' }}>
            {getText("TABLE_STATUS_LABEL")}
            </div>
          ),
        size: 150,
        meta: { headerClassName: 'text-end', className: 'text-end status-column' },
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <TemplateStatusSwitch
              templateId={rowData.id}
              currentStatus={rowData.status}
              fetchTemplates={fetchTemplates}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        size: 100,
        enableSorting: false,
        cell: (info) => {
          const rowData = info.row.original;
          const templateId = rowData.id;
      
          return (
            <div className="hstack gap-2 justify-content-end">
              {/* GÖRÜNTÜLE BUTONU */}
              {canView && (
                <Link to={`/mail-templates/view/${templateId}`}
                  className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                  title={getText("VIEW")}>
                  <FiEye />
                </Link>
              )}
              {canEdit && (
                <Link to={`/mail-templates/edit/${templateId}`}
                  className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                  title={getText("EDIT")}>
                  <FiEdit3 />
                </Link>
              )}
              {canDelete && (
                <button
                  className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                  onClick={() => handleSoftDelete(templateId)}
                  title={getText("DELETE")}>
                  <FiTrash2 />
                </button>
              )}

            </div>
          );
        },
      },
      
      
    ],
    [translations]);

    if (!isTranslationsReady) return null;

  // Yeni şablon ekleme
  const addTemplate = async () => {
    if (!newTemplate.title || !newTemplate.content) {
      toast.warn(getText("WARNING_REQUIRED_FIELDS"));
      return;
    }
    try {
      await axios.post(BASE_URL, newTemplate);
      toast.success(getText("SUCCESS_TEMPLATE_SAVED"));
      // Formu sıfırla
      setNewTemplate({ title: "", content: "", status: "active" });
      // Listeyi yenile
      fetchTemplates();
    } catch (error) {
      console.error("Şablon eklenemedi:", error);
      toast.error(getText("ERROR_TEMPLATE_NOT_ADDED"));
    }
  };
  
  const deleteTemplate = (id) => {
    confirmAlert({
      title: getText("DELETE_CONFIRM_TITLE", "Şablonu Silmek Üzeresiniz"),
      message: getText("DELETE_CONFIRM_MSG", "Bu mail şablonunu silmek istediğinizden emin misiniz?"),
      buttons: [
        {
          label: getText("YES_DELETE", "Evet, Sil"),
          onClick: async () => {
            try {
              await axios.delete(`${BASE_URL}/${id}`);
              toast.success(getText("SUCCESS_TEMPLATE_DELETED", "Şablon silindi!"));
              fetchTemplates();
            } catch (error) {
              console.error("Şablon silinemedi:", error);
              toast.error(getText("ERROR_TEMPLATE_DELETE_FAILED", "Silme işlemi başarısız!"));
            }
          },
        },
        {
          label: getText("CANCEL", "İptal"),
          onClick: () =>
            toast.info(getText("DELETE_CANCELLED", "Silme işlemi iptal edildi.")),
        },
      ],
    });
  };
  

  return (
    <div className="container mt-1">
      <ToastContainer />

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{getText("LOADING")}</span>
          </div>
        </div>
      ) : (
        <Table data={templates} columns={columns} />
      )}

        </div>
     
   
  );
};

export default MailTemplates;
